import * as d3scale from 'd3-scale';
import { DataTable, ValueAccessor } from './models';
import { DefaultMeasureAccessor, DefaultTimestampAccessor } from './models/Accessor';
import { isNullOrUndefined } from './models/DataTable';

export interface HitMaps {
  readonly mapOfDataPointIndexToCoords: ReadonlyArray<{ readonly x: number; readonly y: number | undefined }>;
  readonly mapOfPixelXCoordsToIndexInDataPoints: Uint16Array;
  readonly timeScale: (value: Date) => number;
  readonly measureScale: (value: number) => number;
}

/**
 * This function calculates two maps:
 *
 * 1. A map of data points to on screen coordinates
 * 2. A map x pixel coordinates (within the given area) that correspond to data points
 *
 * To do this, it uses the passed in area and domains to first generate d3
 * scales for both x and y axes. Calculating the first map is a simple `map`
 * operation over the data table, using each of the scales.
 *
 * The second map is handled by `generateMapOfPixelXCoordsToIndexInDataPoints`,
 * which is explained in detail below.
 *
 * @param data
 * @param width
 * @param height
 * @param timeDomain
 * @param measureDomain
 */
export const calculateHitMaps = (
  data: DataTable,
  width: number,
  height: number,
  timeDomain: readonly [Date, Date],
  measureDomain: readonly [number, number],
  additionalPixelsForFinalPoint: number = 0,
  measureAccessor: ValueAccessor<number | undefined> = DefaultMeasureAccessor,
  timestampAccessor: ValueAccessor<Date> = DefaultTimestampAccessor
): HitMaps => {
  const timeScale = d3scale
    .scaleLinear()
    .domain(timeDomain as [Date, Date])
    .range([0, width]);

  const measureScale = d3scale
    .scaleLinear()
    .domain(measureDomain as [number, number])
    .range([height, 0]);

  const mapOfDataPointIndexToCoords = data.map((p) => {
    const measure = measureAccessor(p);
    return {
      x: timeScale(timestampAccessor(p)),
      y: isNullOrUndefined(measure) ? undefined : measureScale(measure),
    };
  });

  const mapOfPixelXCoordsToIndexInDataPoints = generateMapOfPixelXCoordsToIndexInDataPoints(
    data,
    width,
    timeScale,
    additionalPixelsForFinalPoint,
    measureAccessor,
    timestampAccessor
  );

  return {
    mapOfPixelXCoordsToIndexInDataPoints,
    mapOfDataPointIndexToCoords,
    timeScale,
    measureScale,
  };
};

/**
 * This function generates the map of input pixels to data points.
 *
 * The algorithm determines the pixel distance between two data points using the
 * passed in scale, and then uses that to distribute pixels for each data point.
 * Ideally, each point will own the space half way to either neighbor. This
 * means that the first and last data point have roughly half the alloted space
 * when compared with inner data points. To ensure it is always possible to
 * scrub to the final data point, we reserve all pixels to the right of the
 * final point, all the way to the edge of the screen.
 *
 * @param data The data table
 * @param width the width of the area the data * is plotted in
 * @param timeScale the d3 scale used to plot the data
 */
export const generateMapOfPixelXCoordsToIndexInDataPoints = (
  data: DataTable,
  width: number,
  timeScale: (d: Date) => number,
  additionalPixelsForFinalPoint: number = 0,
  measureAccessor: ValueAccessor<number | undefined> = DefaultMeasureAccessor,
  timestampAccessor: ValueAccessor<Date> = DefaultTimestampAccessor
): Uint16Array => {
  if (data.length === 0) {
    return new Uint16Array(0);
  }

  if (additionalPixelsForFinalPoint < 0) {
    additionalPixelsForFinalPoint = 0;
  }

  if (width < 0) {
    width = 0;
  }

  const totalWidth = Math.round(width + additionalPixelsForFinalPoint);

  if (totalWidth <= 0) {
    return new Uint16Array(0);
  }

  const mapOfPixelXCoordsToIndexInDataPoints = new Uint16Array(totalWidth);
  if (data.length < 2) {
    return mapOfPixelXCoordsToIndexInDataPoints;
  }

  let previousIdealPixelForPoint = 0;
  let currentIdealPixelForPoint = Math.floor(timeScale(timestampAccessor(data[0])));

  const calculatePixelRange = (index: number) => {
    const pixelToStartAt =
      index === 0
        ? 0
        : previousIdealPixelForPoint + Math.ceil((currentIdealPixelForPoint - previousIdealPixelForPoint) / 2);

    if (index === data.length - 1) {
      return [Math.min(pixelToStartAt, Math.floor(width) - 1), totalWidth - 1];
    }

    const nextPointIdealPixel = Math.floor(timeScale(timestampAccessor(data[index + 1])));

    const pixelToStopAt = currentIdealPixelForPoint + Math.floor((nextPointIdealPixel - currentIdealPixelForPoint) / 2);

    previousIdealPixelForPoint = currentIdealPixelForPoint;
    currentIdealPixelForPoint = nextPointIdealPixel;

    return [pixelToStartAt, Math.min(pixelToStopAt, totalWidth - 1)];
  };

  const shouldWritePixel = (index: number, pixel: number) => {
    const isLastPoint = () => index === data.length - 1;
    const isFirstPixel = () => pixel === 0;
    const hasntBeenWrittenBefore = () => mapOfPixelXCoordsToIndexInDataPoints[pixel] === 0;
    const hasMeasure = () => !isNullOrUndefined(measureAccessor(data[index]));
    const hasLargerDeltaThanPreviousPoint = () => {
      const pointAlreadyWrittenToPixel = data[mapOfPixelXCoordsToIndexInDataPoints[pixel] - 1];

      if (pointAlreadyWrittenToPixel === undefined) {
        // There is no point already written so treat the current point as larger.
        return true;
      }

      const measureAlreadyWrittenToPixel = measureAccessor(pointAlreadyWrittenToPixel);
      if (isNullOrUndefined(measureAlreadyWrittenToPixel)) {
        return true;
      }

      const currentMeasure = measureAccessor(data[index]);

      const pointOfPreviousPixel = data[mapOfPixelXCoordsToIndexInDataPoints[pixel - 1] - 1];

      if (pointOfPreviousPixel === undefined) {
        // There is no previous pixel so default to this point.
        return true;
      }

      const measureOfPreviousPixel = measureAccessor(pointOfPreviousPixel);

      if (isNullOrUndefined(measureOfPreviousPixel) || isNullOrUndefined(currentMeasure)) {
        return false;
      }

      const currentDelta = Math.abs(currentMeasure - measureOfPreviousPixel);
      const previousDelta = Math.abs(measureAlreadyWrittenToPixel - measureOfPreviousPixel);

      return currentDelta > previousDelta;
    };

    return (
      hasMeasure() && (isLastPoint() || isFirstPixel() || hasntBeenWrittenBefore() || hasLargerDeltaThanPreviousPoint())
    );
  };

  data.forEach((_, index) => {
    const [pixelToStartAt, pixelToStopAt] = calculatePixelRange(index);
    for (let p = pixelToStartAt; p <= pixelToStopAt; p++) {
      if (shouldWritePixel(index, p)) {
        mapOfPixelXCoordsToIndexInDataPoints[p] = index + 1;
      }
    }
  });

  // We stored the 'index + 1' so we could tell which values have been set or not.
  // Make this the actual index now.
  for (let i = 0; i < totalWidth; i++) {
    mapOfPixelXCoordsToIndexInDataPoints[i] = mapOfPixelXCoordsToIndexInDataPoints[i] - 1;
  }

  return mapOfPixelXCoordsToIndexInDataPoints;
};
